import React, { useEffect, useRef } from 'react';

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TURNSTILE_API_POLL_MS = 50;
const TURNSTILE_API_TIMEOUT_MS = 8000;

let turnstileScriptPromise: Promise<void> | null = null;

function waitForTurnstileApi(): Promise<void> {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();

    const check = (): void => {
      if (window.turnstile && typeof window.turnstile.render === 'function') {
        resolve();
        return;
      }

      if (Date.now() - startedAt >= TURNSTILE_API_TIMEOUT_MS) {
        reject(new Error('Turnstile API did not become ready in time'));
        return;
      }

      window.setTimeout(check, TURNSTILE_API_POLL_MS);
    };

    check();
  });
}

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) {
    return Promise.resolve();
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise;
  }

  const scriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    const script = existingScript ?? document.createElement('script');
    let hasFailed = false;

    const handleError = () => {
      hasFailed = true;
      reject(new Error('Failed to load Turnstile script'));
    };

    script.addEventListener('error', handleError, { once: true });

    if (!existingScript) {
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_SRC;
      script.defer = true;
      document.head.appendChild(script);
    }

    void waitForTurnstileApi()
      .then(() => {
        if (hasFailed) {
          return;
        }

        resolve();
      })
      .catch((error) => {
        if (hasFailed) {
          return;
        }

        reject(error);
      });
  }).catch((error) => {
    turnstileScriptPromise = null;
    throw error;
  });

  turnstileScriptPromise = scriptPromise;
  return scriptPromise;
}

interface TurnstileWidgetProps {
  siteKey: string;
  language: string;
  resetNonce: number;
  onTokenChange: (token: string) => void;
  onWidgetError: (errorCode?: string) => void;
}

function TurnstileWidget({
  siteKey,
  language,
  resetNonce,
  onTokenChange,
  onWidgetError,
}: TurnstileWidgetProps): React.ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    let isActive = true;
    onTokenChange('');

    void loadTurnstileScript()
      .then(() => {
        if (!isActive || !window.turnstile || !containerRef.current) {
          return;
        }

        if (widgetIdRef.current) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }

        const widgetId = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: 'session_start',
          language,
          theme: 'auto',
          callback: (token: string) => {
            onTokenChange(token);
          },
          'expired-callback': () => {
            onTokenChange('');
            if (widgetIdRef.current && window.turnstile) {
              window.turnstile.reset(widgetIdRef.current);
            }
          },
          'error-callback': (errorCode: string) => {
            onTokenChange('');
            onWidgetError(errorCode);
          },
          'timeout-callback': () => {
            onTokenChange('');
            if (widgetIdRef.current && window.turnstile) {
              window.turnstile.reset(widgetIdRef.current);
            }
          },
        });

        widgetIdRef.current = widgetId;
      })
      .catch(() => {
        if (!isActive) {
          return;
        }

        onTokenChange('');
        onWidgetError();
      });

    return () => {
      isActive = false;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [siteKey, language, onTokenChange, onWidgetError]);

  useEffect(() => {
    if (!widgetIdRef.current || !window.turnstile) {
      return;
    }

    onTokenChange('');
    window.turnstile.reset(widgetIdRef.current);
  }, [resetNonce, onTokenChange]);

  return <div className="welcome-block__turnstile" ref={containerRef} />;
}

export default TurnstileWidget;
